/**
 * ============================================================================
 * COLORS — central brand palette for the Sales & Marketing MFE.
 *
 * Reference design: LIGHT main content area with a DARK sidebar.
 * The main palette is light mode. The `sidebar` object below is intentionally
 * kept separate because the sidebar does not follow the main palette — it
 * retains the dark background / light text / red accent from the brand.
 *
 * NOTE (palette refresh): sidebar background, page background, and the red
 * accent scale were updated to the current design tokens (dark slate sidebar
 * #14242C, page bg #F8FAFC, Tailwind red-400/500/600 accent scale). These
 * values intentionally diverge from the shell's older navy palette per the
 * updated reference design. If the shell re-syncs its palette, mirror these.
 * ============================================================================
 */

/**
 * Tailwind-matched red accent scale. The design calls out red-400/500/600 for
 * active nav state, card borders, CTA buttons, and logout text. These are the
 * exact Tailwind v3 hex values so MUI `sx` usage and Tailwind utility classes
 * resolve to the same colors.
 */
export const red = {
  400: '#F87171', // tailwind red-400
  500: '#EF4444', // tailwind red-500
  600: '#DC2626', // tailwind red-600
  700: '#B91C1C', // tailwind red-700 — darker scrollbar thumb
  800: '#991B1B', // tailwind red-800 — scrollbar thumb hover
};

/**
 * Blue accent scale used by the Sales dashboard filter bar + KPI tint (per the
 * updated reference). Intentionally scoped to Sales for now: `#2563EB` for
 * dropdown borders, the Apply button, and active/open text; `#EFF6FF` for the
 * light KPI card tint; `#DBEAFE` for the selected-row highlight and `#EEF6FF`
 * for row hover in the employee table.
 */
export const blue = {
  600: '#2563EB', // borders / Apply / active text
  tint: '#EFF6FF', // KPI card light-blue tint
  rowSelected: '#DBEAFE', // selected employee row
  rowHover: '#EEF6FF', // employee row hover
};

export const brand = {
  // Dark slate sidebar surface + a slightly lighter hover row.
  sidebarBg: '#14242C',
  sidebarHover: '#1E293B',

  // Blue accent (Sales dashboard scope).
  blue: blue[600],
  blueTint: blue.tint,

  // Accent red scale (Tailwind red-400/500/600).
  red: red[500],
  redLight: red[400],
  redDark: red[600],

  // Light-mode surfaces / text.
  pageBg: '#F8FAFC',
  paper: '#FFFFFF',
  border: '#E2E8F0',
  textPrimary: '#1A2332',
  textSecondary: '#64748B',

  // --- Back-compat aliases (older code referenced navy*). Kept pointing at
  // the new slate values so nothing that imported them breaks. ---
  navy: '#14242C',
  navyDeep: '#0E1A20',
  navyLight: '#1E293B',
};

/**
 * Sidebar-specific palette. The sidebar is dark regardless of the light main
 * theme, so these values are consumed directly by DeptSidebar rather than
 * through the MUI palette.
 */
export const sidebar = {
  background: '#0F172A',
  backgroundDeep: '#0B1226',
  hover: 'rgba(255, 255, 255, 0.06)',
  text: '#F5F7FA',
  textMuted: '#94A3B8',
  accent: '#4A1420',
  accentHover: '#5C1A2A',
  divider: 'rgba(148, 163, 184, 0.16)',
  subtitleColor: '#94A3B8',
};

/**
 * Reusable card style token. Matches the design spec: white background, 2px
 * colored border, rounded-2xl (16px), subtle shadow, and a hover state that
 * raises the shadow and switches the border to the red accent. Consumed via
 * MUI `sx` on hub/landing cards. Keep in sync with Tailwind's rounded-2xl.
 */
export const card = {
  radius: 16, // rounded-2xl
  borderWidth: 2,
  borderColor: brand.border,
  borderColorHover: red[500],
  background: brand.paper,
  shadow: '0 1px 2px rgba(16, 24, 40, 0.06), 0 1px 3px rgba(16, 24, 40, 0.10)', // shadow-sm
  shadowHover: '0 10px 15px -3px rgba(16, 24, 40, 0.12), 0 4px 6px -4px rgba(16, 24, 40, 0.10)', // shadow-lg
};

/**
 * Hub-page background tokens. Dark navy base with two clean radial glows —
 * blue in the top-left corner (~450px spread), dark red in the bottom-right.
 * No linear blend or purple tint. The `background` shorthand is consumed
 * directly by HubPage.jsx as a single CSS `background` property.
 */
export const hubPage = {
  base: '#0B1226',
  background: [
    // Blue glow, top-left. Circle at 12% 6% — visible across the top-left
    // ~450px of the content area. Farthest-corner sizing so the gradient
    // scales naturally with the viewport.
    'radial-gradient(circle at 12% 6%, rgba(30,64,175,0.85) 0%, rgba(30,64,175,0.35) 28%, transparent 55%)',
    // Dark red glow, bottom-right corner.
    'radial-gradient(circle at 96% 100%, rgba(153,27,27,0.9) 0%, rgba(127,29,29,0.4) 30%, transparent 55%)',
    '#0B1226',
  ].join(', '),
};

export const colors = {
  primary: {
    main: brand.sidebarBg,
    dark: brand.navyDeep,
    light: brand.sidebarHover,
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: red[500],
    light: red[400],
    dark: red[600],
    contrastText: '#FFFFFF',
  },
  background: {
    default: brand.pageBg,
    paper: brand.paper,
  },
  text: {
    primary: brand.textPrimary,
    secondary: brand.textSecondary,
  },
  divider: brand.border,
};
